// HCEN Patient Website — Location Service
// Same role as Flutter's LocationService: GPS, IP fallback, reverse geocode, distance.
// Used by LocationContext (global state) and can be imported anywhere.

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

function fetchWithTimeout(url, ms = 5000, opts = {}) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { signal: ctrl.signal, ...opts })
    .finally(() => clearTimeout(t));
}

/** IP-based city (±50 km accuracy — fallback only). */
export async function ipLookup() {
  try {
    const r = await fetchWithTimeout('https://ipapi.co/json/', 5000);
    if (!r.ok) return null;
    const j = await r.json();
    if (!j?.latitude || !j?.longitude) return null;
    return {
      coords: { lat: Number(j.latitude), lng: Number(j.longitude) },
      city: j.city || j.region || '',
      area: j.region || '',
      source: 'ip',
      accuracy: 50000,
      detectedAt: Date.now(),
    };
  } catch {
    return null;
  }
}

/** Reverse geocode lat/lng → city name. Google first, then OpenStreetMap (free). */
export async function reverseGeocode(lat, lng) {
  if (GMAPS_KEY) {
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GMAPS_KEY}&result_type=locality|administrative_area_level_2|administrative_area_level_3`;
      const r = await fetchWithTimeout(url, 5000);
      if (r.ok) {
        const j = await r.json();
        if (j.status === 'OK' && j.results?.length) {
          const comps = j.results[0].address_components || [];
          const pick = (...types) => {
            for (const t of types) {
              const c = comps.find(x => (x.types || []).includes(t));
              if (c) return c.long_name;
            }
            return '';
          };
          const city = pick('locality', 'administrative_area_level_2', 'administrative_area_level_3');
          const area = pick('sublocality', 'sublocality_level_1', 'neighborhood');
          if (city) return { city, area, formatted: j.results[0].formatted_address || '' };
        }
      }
    } catch { /* fall through */ }
  }

  // Free fallback — no API key required
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=12&addressdetails=1`;
    const r = await fetchWithTimeout(url, 6000, {
      headers: { Accept: 'application/json', 'User-Agent': 'HCEN-Patient-Website/1.0' },
    });
    if (!r.ok) return null;
    const j = await r.json();
    const a = j.address || {};
    const city = a.city || a.town || a.village || a.county
      || a.state_district || a.suburb || '';
    const area = a.suburb || a.neighbourhood || a.road || '';
    if (city || j.display_name) {
      return {
        city: city || j.display_name.split(',')[0],
        area,
        formatted: j.display_name || '',
      };
    }
  } catch { /* ignore */ }

  return null;
}

/** Forward geocode city name → approximate coords (manual city pick). */
export async function forwardGeocode(cityName) {
  if (!cityName?.trim()) return null;
  if (GMAPS_KEY) {
    try {
      const q = encodeURIComponent(`${cityName}, India`);
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${q}&key=${GMAPS_KEY}`;
      const r = await fetchWithTimeout(url, 5000);
      if (r.ok) {
        const j = await r.json();
        if (j.status === 'OK' && j.results?.[0]?.geometry?.location) {
          const { lat, lng } = j.results[0].geometry.location;
          return { lat: Number(lat), lng: Number(lng) };
        }
      }
    } catch { /* fall through */ }
  }

  try {
    const q = encodeURIComponent(`${cityName}, Andhra Pradesh, India`);
    const url = `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`;
    const r = await fetchWithTimeout(url, 6000, {
      headers: { Accept: 'application/json', 'User-Agent': 'HCEN-Patient-Website/1.0' },
    });
    if (!r.ok) return null;
    const arr = await r.json();
    if (arr?.[0]) {
      return { lat: Number(arr[0].lat), lng: Number(arr[0].lon) };
    }
  } catch { /* ignore */ }

  return null;
}

/** Browser GPS — returns { lat, lng, accuracy } or throws GeolocationPositionError. */
export function getGpsPosition({ highAccuracy = true, timeout = 15000, maxAge = 0 } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy || 0,
      }),
      err => reject(err),
      { enableHighAccuracy: highAccuracy, timeout, maximumAge: maxAge },
    );
  });
}

/** Haversine distance in km. */
export function distanceKm(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some(v => v == null || !Number.isFinite(Number(v)))) return null;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function isAccurateLocation(loc) {
  return loc?.source === 'gps' && loc?.coords
    && (loc.accuracy == null || loc.accuracy < 5000);
}
